import CustomRecurrenceModal, { RecurrenceFrequencyApi } from '@/components/CustomRecurrenceModal/CustomRecurrenceModal';
import { CustomRecurrenceData } from '@/gql/events/types';
import { IconClear, IconRepeat } from '@/theme/icons';
import { formatRecurrenceChipLabel } from '@/utils/events';
import rem from '@/utils/rem';
import { Box, Button, Flex, Icon, Spinner, StyleProps, Text } from '@chakra-ui/react';
import { MouseEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

type RecurrenceMenuProps = {
  onOpen?: () => void;
  onClose?: () => void;
  isLoading?: boolean;
  categoryId?: string;
  recurrence: string | null;
  setSelectedRecurrence?: (recurrence: string) => void;
  setCustomRecurrence?: (recurrence: CustomRecurrenceData | undefined) => void;
  readonly?: boolean;
  width?: StyleProps['width'];
  showCategoryIcon?: boolean;
  variant?: 'default' | 'bordered';
  interval?: number;
  byDays?: { day: string }[];
  occurrences?: number;
  scheduleDate?: Date;
  recurrenceFrequency?: RecurrenceFrequencyApi;
  /** Saved "Ends on" date when Ends = "On Date"; restores state when reopening modal */
  endDate?: Date | null;
};

const ReccurenceMenu = ({
  onOpen,
  onClose,
  isLoading = false,
  recurrence,
  setSelectedRecurrence,
  setCustomRecurrence,
  readonly,
  width = rem(220),
  variant = 'default',
  interval = 1,
  byDays,
  occurrences,
  scheduleDate,
  recurrenceFrequency,
  endDate,
}: RecurrenceMenuProps) => {
  const { t } = useTranslation('RecurrenceMenu');
  const [isOpenCustomRecurrenceModal, setIsOpenCustomRecurrenceModal] = useState(false);
  const [prevRecurrence, setPrevRecurrence] = useState<string | null>(null);

  const handleOpenModal = useCallback(() => {
    setPrevRecurrence(recurrence);
    onOpen && onOpen();
    setIsOpenCustomRecurrenceModal(true);
  }, [recurrence, onOpen]);

  const handleSelectCustomRecurrence = useCallback(
    (customRecurrence: CustomRecurrenceData) => {
      setCustomRecurrence && setCustomRecurrence(customRecurrence);
      const chipLabel = formatRecurrenceChipLabel(customRecurrence, scheduleDate);
      if (chipLabel && setSelectedRecurrence) setSelectedRecurrence(chipLabel);
      onClose && onClose();
      setIsOpenCustomRecurrenceModal(false);
    },
    [scheduleDate, setCustomRecurrence, setSelectedRecurrence, onClose],
  );

  const handleClearRecurrence = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setSelectedRecurrence && setSelectedRecurrence('');
      setCustomRecurrence && setCustomRecurrence(undefined);
    },
    [setSelectedRecurrence, setCustomRecurrence],
  );

  const onCloseCustomRecurrenceModal = useCallback(() => {
    if (setSelectedRecurrence && prevRecurrence !== null) {
      setSelectedRecurrence(prevRecurrence || '');
    }
    onClose && onClose();
    setIsOpenCustomRecurrenceModal(false);
  }, [prevRecurrence, setSelectedRecurrence, onClose]);

  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" width={rem(160)}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <>
      <Box position="relative" width={width} lineHeight={0}>
        <Button
          justifyContent="flex-start"
          width={width}
          height={rem(28)}
          color="text-primary"
          opacity={readonly ? 0.5 : 1}
          border={variant === 'bordered' ? `${rem(1)} solid var(--chakra-colors-stroke-primary)` : undefined}
          borderRadius={rem(4)}
          _hover={{
            backgroundColor: 'background-quaternary',
          }}
          _active={{
            backgroundColor: 'background-secondary',
          }}
          pointerEvents={readonly ? 'none' : 'visible'}
          backgroundColor="background-primary"
          onClick={handleOpenModal}
          paddingX={rem(8)}
        >
          <Flex
            alignItems="center"
            justifyContent="flex-start"
            flex={1}
            gap={2}
            minWidth={0}
            height={rem(24)}
          >
            <Icon as={IconRepeat} boxSize={rem(12)} color="currentColor" />

            <Text
              textStyle="small"
              overflow="hidden"
              color="text-primary"
              textAlign="left"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {recurrence || t('labels.doesNotRepeat')}
            </Text>
          </Flex>
        </Button>

        {recurrence && !readonly && (
          <Button
            position="absolute"
            top="50%"
            right={rem(8)}
            alignItems="center"
            justifyContent="center"
            display="flex"
            width={rem(16)}
            minWidth={rem(16)}
            height={rem(16)}
            padding="0"
            borderRadius="full"
            _hover={{
              backgroundColor: 'background-quaternary',
            }}
            transform={`translateY(-50%)`}
            cursor="pointer"
            aria-label={t('ariaLabels.clearRecurrence')}
            backgroundColor="background-tertiary"
            onClick={handleClearRecurrence}
            variant="unstyled"
          >
            <Icon as={IconClear} boxSize={rem(8)} color="icon-secondary" />
          </Button>
        )}
      </Box>
      <CustomRecurrenceModal
        currentDate={scheduleDate}
        byDays={byDays}
        occurrences={occurrences}
        interval={interval}
        recurrence={recurrence ?? undefined}
        initialFrequency={recurrenceFrequency}
        endDate={endDate}
        isOpen={isOpenCustomRecurrenceModal}
        onSave={handleSelectCustomRecurrence}
        onClose={onCloseCustomRecurrenceModal}
      />
    </>
  );
};

export default ReccurenceMenu;
